/**
 * ============================================================================
 * EXPERIMENT: Calendly Integration (ab_calendly)
 * PR: #TODO_AFTER_PR_CREATION_ab_calendly
 *
 * OAuth callback page. Receives ?code= from Calendly, exchanges it for a token,
 * stores it in localStorage, then redirects back to the app.
 * Temporary code — will be REMOVED when the experiment concludes.
 *
 * Find all experiment code: grep -r "ab_calendly" src/
 * ============================================================================
 */

import { Button } from '@evaneos/b2b-ui';
import { useReducer, useEffect, useRef } from 'react';

import { exchangeCodeForToken } from '../../resources/calendly/AB_calendly-auth';
import { history } from '../../shared/routing/browserHistory';

type CallbackState =
    | { status: 'loading' }
    | { status: 'success' }
    | { status: 'error'; message: string };

type Action = { type: 'success' } | { type: 'error'; message: string };

function reducer(_state: CallbackState, action: Action): CallbackState {
    switch (action.type) {
        case 'success':
            return { status: 'success' };
        case 'error':
            return { status: 'error', message: action.message };
    }
}

function getCodeFromUrl(): string | null {
    return new URLSearchParams(window.location.search).get('code');
}

function getInitialState(): CallbackState {
    const code = getCodeFromUrl();
    if (!code) {
        return { status: 'error', message: 'No authorization code received from Calendly.' };
    }
    return { status: 'loading' };
}

export function AB_CalendlyCallback(): JSX.Element {
    const [state, dispatch] = useReducer(reducer, null, getInitialState);
    const hasExchanged = useRef(false);

    useEffect(() => {
        const code = getCodeFromUrl();
        if (!code || hasExchanged.current) return;
        hasExchanged.current = true;

        let redirectTimer: ReturnType<typeof setTimeout>;

        exchangeCodeForToken(code)
            .then(() => {
                dispatch({ type: 'success' });
                redirectTimer = setTimeout(() => history.push('/homepage'), 1500);
            })
            .catch((err: unknown) => {
                dispatch({
                    type: 'error',
                    message: err instanceof Error ? err.message : 'Unknown error',
                });
            });

        return () => clearTimeout(redirectTimer);
    }, []);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '60vh',
                gap: '16px',
            }}
        >
            {state.status === 'loading' && (
                <p style={{ fontSize: '1.6rem', color: 'var(--white)' }}>
                    Connexion à Calendly en cours...
                </p>
            )}
            {state.status === 'success' && (
                <>
                    <p style={{ fontSize: '1.6rem', color: 'var(--color-green-100)' }}>
                        Calendly connecté avec succès !
                    </p>
                    <p style={{ fontSize: '1.4rem', color: 'var(--white)' }}>
                        Redirection en cours...
                    </p>
                </>
            )}
            {state.status === 'error' && (
                <>
                    <p style={{ fontSize: '1.6rem', color: 'var(--color-red-200)' }}>
                        Erreur de connexion Calendly
                    </p>
                    <p style={{ fontSize: '1.4rem', color: 'var(--white)' }}>{state.message}</p>
                    <Button
                        variant="outlined"
                        color="inherit"
                        onClick={() => history.push('/homepage')}
                        sx={{ marginTop: '8px' }}
                    >
                        Retour
                    </Button>
                </>
            )}
        </div>
    );
}
